import {
    Box,
    Button,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Assignment, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import { BotaoVoltar } from "./BotaoVoltar"
import { ContratoAplicativoActionsTypes } from "../context/contratoAplicativoActions"
import { useContratoAplicativoContext } from "../context/useContratoAplicativoContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(3) },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            borderRadius: 15,
            marginBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb), 0.3)",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb), 0.4)",
        },
        [theme.breakpoints.down("sm")]: { width: "100%" },
    },
    somenteLeituraAviso: {
        color: "#e65100",
        fontWeight: 500,
        marginBottom: theme.spacing(2),
    },
}))

export function FormularioContratoAplicativo() {
    const classes = useStyles()
    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, erroCarregamento } = useContratoAplicativoContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const desabilitado = !!erroCarregamento || !dados.podeEditar

    const handleCampo = (campo: keyof typeof dados) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        dispatch({ type: ContratoAplicativoActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor: value } })
    }

    const handleVendedor = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        dispatch({
            type: ContratoAplicativoActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo: "idVendedor", valor: value !== "" ? Number(value) : null },
        })
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Dados do contrato salvos com sucesso!", "success")
        } catch (err) {
            alertStart((err as Error).message || "Erro ao salvar dados do contrato", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <BotaoVoltar />
                <Box className={classes.header}>
                    <Typography variant={isSmall ? "h5" : "h4"} className={classes.titleText}>
                        Dados do Contrato
                    </Typography>
                </Box>

                {dados.podeEditar === false && (
                    <Typography className={classes.somenteLeituraAviso}>
                        Este estabelecimento não possui um aplicativo próprio. Os dados são somente leitura.
                    </Typography>
                )}

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Assignment className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Dados do Contratante
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Razão Social"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.razaoSocial}
                                    onChange={handleCampo("razaoSocial")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nome Fantasia"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.nomeFantasia}
                                    onChange={handleCampo("nomeFantasia")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CNPJ"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.cnpj}
                                    onChange={handleCampo("cnpj")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nome do Responsável"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.nomeResponsavel}
                                    onChange={handleCampo("nomeResponsavel")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="E-mail do Responsável"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="email"
                                    value={dados.emailResponsavel}
                                    onChange={handleCampo("emailResponsavel")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Endereço"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.endereco}
                                    onChange={handleCampo("endereco")}
                                    disabled={desabilitado}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Cadastrado por"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.idVendedor ?? ""}
                                    onChange={handleVendedor}
                                    disabled={desabilitado}
                                >
                                    <MenuItem value="">
                                        <em>— Nenhum —</em>
                                    </MenuItem>
                                    {dados.vendedores.map(v => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<Save />}
                        onClick={handleSalvar}
                        disabled={desabilitado}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
